import { useEffect, useMemo, useState, type ReactNode } from "react";
import { AppShell, TopHeader } from "../components/AppShell";
import { Button, Card, Pill, SectionLabel } from "../components/ui";
import { Ic } from "../components/icons";
import { useAuth } from "../context/AuthContext";
import { useNav } from "../context/NavContext";
import { sendEmailVerification, signOut } from "../lib/auth";
import { BrandingTab } from "../components/BrandingTab";
import { useEntitlements } from "../lib/useEntitlements";
import { plans as pricingPlans, priceFor, formatEur } from "../data/pricing";
import {
  openBillingPortal,
  PortalError,
  reactivateSubscription,
  ReactivateError,
} from "../lib/checkout";
import { PlanSwitcher } from "../components/PlanSwitcher";
import type { PlanId } from "../data/pricing";
import {
  RateLimitError,
  deleteAccountDefinitively,
  exportAndDownloadUserData,
  reauthenticateCurrentUser,
} from "../lib/privacy";

type TabId =
  | "profil"
  | "branding"
  | "equipe"
  | "abonnement"
  | "donnees"
  | "preferences";

const VALID_TABS: readonly TabId[] = [
  "profil",
  "branding",
  "equipe",
  "abonnement",
  "donnees",
  "preferences",
];

type TabDef = { id: TabId; label: string; icon: ReactNode };
type TabGroup = { label: string; items: TabDef[] };

// Sub-nav settings groupée — calque les patterns Stripe / Linear / Vercel.
// L'ordre des groupes reflète l'ordre attendu d'usage (compte d'abord,
// billing, puis personnalisation).
const tabGroups: TabGroup[] = [
  {
    label: "Compte",
    items: [
      { id: "profil", label: "Profil", icon: <Ic.User size={14} /> },
      { id: "preferences", label: "Préférences", icon: <Ic.Settings size={14} /> },
      { id: "donnees", label: "Données personnelles", icon: <Ic.Shield size={14} /> },
    ],
  },
  {
    label: "Facturation",
    items: [
      { id: "abonnement", label: "Abonnement", icon: <Ic.Wallet size={14} /> },
    ],
  },
  {
    label: "Espace de travail",
    items: [
      { id: "branding", label: "Branding", icon: <Ic.Palette size={14} /> },
      { id: "equipe", label: "Équipe", icon: <Ic.Users size={14} /> },
    ],
  },
];

// Liste plate dérivée — sert au mobile fallback (chips horizontales) et à
// d'éventuels lookups par id sans avoir à nester.
const tabs: TabDef[] = tabGroups.flatMap((g) => g.items);

export default function Settings() {
  const { user, loading } = useAuth();
  const { go, settingsTab } = useNav();
  const [tab, setTab] = useState<TabId>(() =>
    settingsTab && (VALID_TABS as readonly string[]).includes(settingsTab)
      ? (settingsTab as TabId)
      : "profil",
  );

  // Si NavContext.settingsTab change après le mount (ex. user clique sur
  // "Gérer mon abonnement" depuis le Dashboard, ce qui appelle
  // go("settings", { settingsTab: "abonnement" })), on suit. Tant que la
  // valeur est null, on laisse l'user libre de naviguer entre les tabs.
  useEffect(() => {
    if (
      settingsTab &&
      (VALID_TABS as readonly string[]).includes(settingsTab)
    ) {
      setTab(settingsTab as TabId);
    }
  }, [settingsTab]);

  return (
    <AppShell active="settings">
      <TopHeader />

      <div className="flex flex-col md:flex-row page-enter">
        {/* Sub-nav latérale (desktop md+) — sticky sous le TopHeader, expose
            les groupes de paramètres. Sur mobile, masquée au profit de chips
            horizontales en haut du contenu. */}
        <aside
          className="hidden md:flex md:flex-col md:w-[220px] md:shrink-0 md:sticky md:top-16 md:self-start md:h-[calc(100vh-4rem)] border-r border-ink-200/70 bg-paper/40 px-3 py-6 overflow-y-auto"
        >
          <div className="px-2 mb-5">
            <SectionLabel>Compte</SectionLabel>
            <h1 className="mt-1 text-[20px] font-semibold tracking-tight">
              Paramètres
            </h1>
          </div>
          <nav className="flex-1 space-y-5">
            {tabGroups.map((group) => (
              <div key={group.label}>
                <SectionLabel className="px-2">{group.label}</SectionLabel>
                <ul className="mt-2 space-y-0.5">
                  {group.items.map((t) => {
                    const active = tab === t.id;
                    return (
                      <li key={t.id}>
                        <button
                          onClick={() => setTab(t.id)}
                          className={`w-full flex items-center gap-2.5 h-9 px-2.5 rounded-lg text-[13px] transition ${
                            active
                              ? "bg-ink-900 text-white font-medium"
                              : "text-ink-700 hover:bg-ink-100"
                          }`}
                        >
                          <span
                            className={
                              active ? "text-lime-300" : "text-ink-400"
                            }
                          >
                            {t.icon}
                          </span>
                          <span className="truncate">{t.label}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </nav>
        </aside>

        {/* Mobile : titre + chips de tabs horizontales */}
        <div className="md:hidden px-4 sm:px-6 pt-4 pb-3 border-b border-ink-200/70 bg-canvas">
          <div className="mb-3">
            <SectionLabel>Compte</SectionLabel>
            <h1 className="mt-1 text-[22px] font-semibold tracking-tight">
              Paramètres
            </h1>
          </div>
          <div className="flex items-center gap-1.5 overflow-x-auto -mx-1 px-1 pb-1">
            {tabs.map((t) => {
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-full text-[12.5px] whitespace-nowrap shrink-0 transition ${
                    active
                      ? "bg-ink-900 text-white"
                      : "bg-ink-100 text-ink-600"
                  }`}
                >
                  <span className={active ? "text-lime-300" : "text-ink-400"}>
                    {t.icon}
                  </span>
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Contenu de l'onglet actif — utilise la pleine largeur restante.
            Branding garde son cap historique 1280px (besoin d'un canvas
            spécifique), les autres onglets respirent jusqu'à 1100px pour
            préserver la lisibilité du prose. */}
        <div className="flex-1 min-w-0 px-4 sm:px-6 md:px-8 py-4 sm:py-6 md:py-8">
          <div
            className={
              tab === "branding" ? "max-w-[1280px]" : "max-w-[1100px]"
            }
          >
            {tab === "profil" && (
              <ProfilTab
                loading={loading}
                user={user}
                onBackHome={() => go("landing")}
              />
            )}
            {tab === "branding" && <BrandingTab />}
            {tab === "equipe" && <EquipeTab />}
            {tab === "abonnement" && (
              <FacturationTab onSeePricing={() => go("pricing")} />
            )}
            {tab === "donnees" && (
              <DonneesTab
                onShowPolicy={() => {
                  window.location.hash = "#confidentialite";
                  go("legal");
                }}
                onSignedOut={() => go("landing")}
              />
            )}
            {tab === "preferences" && <PreferencesTab />}
          </div>
        </div>
      </div>
    </AppShell>
  );
}

/* ---------- Profil ---------- */

function ProfilTab({
  loading,
  user,
  onBackHome,
}: {
  loading: boolean;
  user: ReturnType<typeof useAuth>["user"];
  onBackHome: () => void;
}) {
  const [signingOut, setSigningOut] = useState(false);
  const [verifSending, setVerifSending] = useState(false);
  const [verifMessage, setVerifMessage] = useState<{
    tone: "success" | "error";
    text: string;
  } | null>(null);

  const provider = useMemo(() => {
    const pid = user?.providerData?.[0]?.providerId ?? "";
    if (pid === "google.com")
      return { id: "google" as const, label: "Google", tone: "lime" as const };
    if (pid === "password")
      return { id: "password" as const, label: "Email", tone: "soft" as const };
    return { id: "other" as const, label: pid || "—", tone: "soft" as const };
  }, [user]);

  if (loading) {
    return (
      <div className="py-14 grid place-items-center">
        <div className="text-[13px] text-ink-500">Chargement…</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="py-14 grid place-items-center">
        <Card className="p-6 sm:p-10 text-center max-w-[440px]">
          <div className="w-14 h-14 rounded-full bg-ink-50 hairline grid place-items-center mx-auto">
            <Ic.User size={18} className="text-ink-500" />
          </div>
          <h2 className="mt-4 text-[22px] font-semibold tracking-tight">
            Connexion requise
          </h2>
          <p className="mt-2 text-[13px] text-ink-600 leading-relaxed">
            Connectez-vous pour accéder à vos paramètres.
          </p>
          <div className="mt-5">
            <Button
              variant="dark"
              onClick={onBackHome}
              iconRight={<Ic.Arrow size={13} />}
            >
              Retour à l’accueil
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const displayName = user.displayName ?? user.email ?? "Utilisateur";
  const initials = user.displayName
    ? user.displayName
        .split(" ")
        .map((s) => s[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : user.email?.slice(0, 2).toUpperCase() ?? "PL";

  const creationDate = user.metadata.creationTime
    ? new Date(user.metadata.creationTime)
    : null;
  const lastSignInDate = user.metadata.lastSignInTime
    ? new Date(user.metadata.lastSignInTime)
    : null;

  async function onSignOut() {
    setSigningOut(true);
    try {
      await signOut();
      onBackHome();
    } finally {
      setSigningOut(false);
    }
  }

  async function handleSendVerification() {
    if (!user) return;
    setVerifSending(true);
    setVerifMessage(null);
    try {
      await sendEmailVerification(user);
      setVerifMessage({
        tone: "success",
        text: `Email envoyé à ${user.email ?? "votre adresse"}.`,
      });
    } catch (e) {
      const err = e as { code?: string; message?: string };
      if (err.code === "auth/too-many-requests") {
        setVerifMessage({
          tone: "error",
          text: "Trop de tentatives — réessayez dans quelques minutes.",
        });
      } else {
        setVerifMessage({
          tone: "error",
          text: err.message ?? "Échec de l'envoi.",
        });
      }
    } finally {
      setVerifSending(false);
    }
  }

  return (
    <div className="space-y-5">
      {/* Header consolidé : avatar + identité + provider badge + sign-out.
          Remplace les 3 cards précédentes (pill orphan + avatar + provider
          card) par un single bloc lisible d'un coup d'œil. */}
      <Card className="p-5 sm:p-6">
        <div className="flex items-start gap-4 sm:gap-5 flex-wrap">
          {user.photoURL ? (
            <img
              src={user.photoURL}
              alt={displayName}
              referrerPolicy="no-referrer"
              className="w-20 h-20 rounded-full object-cover hairline shrink-0"
            />
          ) : (
            <div className="w-20 h-20 rounded-full bg-ink-900 text-lime-300 grid place-items-center text-[22px] font-semibold shrink-0">
              {initials}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-[22px] font-semibold tracking-tight truncate">
                {displayName}
              </h2>
              <Pill tone={provider.tone} icon={<Ic.Bolt size={12} />}>
                {provider.label}
              </Pill>
            </div>
            <div className="mt-1 text-[13.5px] text-ink-600 truncate">
              {user.email}
              {user.emailVerified && (
                <span className="ml-2 text-[11.5px] text-forest font-medium">
                  ✓ vérifié
                </span>
              )}
            </div>
          </div>
          <div className="shrink-0">
            <Button
              variant="soft"
              size="sm"
              onClick={onSignOut}
              disabled={signingOut}
              icon={<Ic.Cross size={13} />}
            >
              {signingOut ? "…" : "Se déconnecter"}
            </Button>
          </div>
        </div>
      </Card>

      {/* 2 colonnes côte à côte : Informations | Sécurité.
          UID Firebase volontairement masqué (info dev — sans utilité user). */}
      <div className="grid lg:grid-cols-2 gap-5">
        <Card className="p-6">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <SectionLabel>Informations personnelles</SectionLabel>
            {provider.id === "google" && (
              <button
                onClick={() =>
                  window.open(
                    "https://myaccount.google.com/personal-info",
                    "_blank",
                    "noopener,noreferrer",
                  )
                }
                className="text-[11.5px] text-ink-500 hover:text-ink-900 inline-flex items-center gap-1 transition"
              >
                Modifier sur Google <Ic.Arrow size={11} />
              </button>
            )}
          </div>
          <dl className="mt-4 space-y-3 text-[13px]">
            <Row label="Nom complet" value={user.displayName ?? "—"} />
            <Row label="Email" value={user.email ?? "—"} />
            <Row
              label="Téléphone"
              value={user.phoneNumber ?? "non renseigné"}
            />
          </dl>
          {provider.id === "google" && (
            <p className="mt-4 pt-4 border-t border-ink-200/70 text-[12px] text-ink-500 leading-relaxed">
              Ces informations proviennent de votre compte Google. Les
              modifications apparaîtront ici après votre prochaine connexion.
            </p>
          )}
        </Card>

        <Card className="p-6">
          <SectionLabel>Sécurité &amp; connexion</SectionLabel>
          <dl className="mt-4 space-y-3 text-[13px]">
            <Row label="Fournisseur" value={provider.label} />
            <Row
              label="Email vérifié"
              value={
                user.emailVerified ? (
                  <span className="text-forest font-medium">Oui</span>
                ) : (
                  <span className="text-amber-700 font-medium">Non</span>
                )
              }
            />
            <Row label="Créé le" value={formatDate(creationDate)} />
            <Row
              label="Dernière connexion"
              value={formatDate(lastSignInDate)}
            />
          </dl>

          {/* CTA inline pour vérifier l'email — uniquement si compte password
              ET non vérifié. Pour Google, l'email est garanti vérifié. */}
          {provider.id === "password" && !user.emailVerified && (
            <div className="mt-5 pt-4 border-t border-ink-200/70">
              <div className="text-[13px] font-medium text-ink-900">
                Vérifiez votre adresse email
              </div>
              <p className="mt-1 text-[12.5px] text-ink-600 leading-relaxed">
                Confirmez que <strong>{user.email}</strong> vous appartient
                pour activer la récupération de mot de passe.
              </p>
              {verifMessage && (
                <div
                  className={`mt-2 text-[12px] font-medium ${
                    verifMessage.tone === "success"
                      ? "text-forest"
                      : "text-rose-700"
                  }`}
                >
                  {verifMessage.tone === "success" ? "✓ " : ""}
                  {verifMessage.text}
                </div>
              )}
              <Button
                variant="soft"
                size="sm"
                onClick={handleSendVerification}
                disabled={verifSending}
                icon={<Ic.Mail size={13} />}
                className="mt-3"
              >
                {verifSending ? "Envoi…" : "Envoyer un email de vérification"}
              </Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

/* ---------- Équipe ---------- */

function EquipeTab() {
  return (
    <EmptyTab
      icon={<Ic.Users size={18} />}
      title="Invitez vos collaborateurs"
      description="Bientôt : invitations par email, rôles (admin / agent / lecture seule), partage des fiches au sein de l'agence."
    />
  );
}

/* ---------- Facturation ---------- */

/**
 * État haut niveau du compte côté UI — différent de `billing.status` (qui
 * reflète Stripe). Permet de driver les copy/labels sans faire 4 ternaires
 * imbriqués dans le JSX.
 *   - subscribed   : sub active OU trialing OU cancel_at_period_end (résiliation prog.)
 *   - pay-as-you-go : pas de sub mais des crédits payés (topup/pack) actifs
 *   - trial        : free, uniquement le freeCredit signup
 *   - inactive     : free, plus rien de valide
 *   - past-due     : sub en échec de paiement (le banner global s'occupe du CTA)
 */
type AccountState =
  | "subscribed"
  | "pay-as-you-go"
  | "trial"
  | "inactive"
  | "past-due";

function FacturationTab({ onSeePricing }: { onSeePricing: () => void }) {
  const { billing, loading, freeCreditDaysLeft, creditsSummary } =
    useEntitlements();
  const [portalBusy, setPortalBusy] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);
  const [reactivateBusy, setReactivateBusy] = useState(false);
  const [reactivateError, setReactivateError] = useState<string | null>(null);

  // A-t-on déjà un Customer Stripe ? C'est le gate pour ouvrir le portail —
  // un utilisateur en essai gratuit n'a pas encore de Customer et verrait
  // juste un message "souscrivez d'abord" si on montrait le bouton.
  const hasStripeCustomer = !!billing?.stripeCustomerId;

  // Résiliation programmée par l'user dans le Customer Portal. cancelAt est
  // posé par le webhook quand `cancel_at_period_end: true` arrive. Tant que
  // l'échéance n'est pas atteinte, status reste "active" — on doit
  // surcharger l'affichage pour signaler que l'abonnement va s'arrêter.
  const cancelling =
    !!billing?.cancelAt && (billing.status === "active" || billing.status === "trialing");

  // Total réel disponible (plan + topup + free) — sert au mini-summary en haut
  // pour répondre à la question "j'ai combien de fiches au total ?". Sur le
  // cas downgrade-keep, ce total dépasse le quota mensuel.
  const totalAvailable = creditsSummary.totalRemaining;

  /** Classification de l'état du compte — voir AccountState. */
  const accountState: AccountState = (() => {
    if (!billing) return "inactive";
    if (billing.status === "past_due") return "past-due";
    if (billing.plan !== "free") return "subscribed";
    if (cancelling) return "subscribed";
    if (creditsSummary.topup > 0) return "pay-as-you-go";
    if (creditsSummary.free > 0) return "trial";
    return "inactive";
  })();

  async function handleOpenPortal() {
    setPortalBusy(true);
    setPortalError(null);
    try {
      await openBillingPortal();
    } catch (e) {
      if (e instanceof PortalError && e.code === "no-customer") {
        setPortalError(
          "Aucun historique de paiement — souscrivez un plan ou achetez un pack d'abord.",
        );
      } else {
        setPortalError(
          e instanceof Error ? e.message : "Impossible d'ouvrir le portail.",
        );
      }
      setPortalBusy(false);
    }
    // En cas de succès, window.location.assign a déjà quitté la page.
  }

  async function handleReactivate() {
    setReactivateBusy(true);
    setReactivateError(null);
    try {
      await reactivateSubscription();
      // Pas besoin de toucher l'UI : le webhook customer.subscription.updated
      // va arriver derrière et reset cancelAt à null. useEntitlements
      // re-render automatiquement.
    } catch (e) {
      if (e instanceof ReactivateError) {
        setReactivateError(e.message);
      } else {
        setReactivateError(
          e instanceof Error ? e.message : "Impossible de réactiver.",
        );
      }
    } finally {
      setReactivateBusy(false);
    }
  }

  // Libellé du SectionLabel en haut de la card — varie selon l'état du
  // compte. "ABONNEMENT ACTIF" est faux pour un free + Pack, donc on
  // bascule sur "STATUT DU COMPTE" générique pour les états pay-as-you-go,
  // trial, inactive.
  const sectionLabel =
    accountState === "subscribed" || accountState === "past-due"
      ? "Abonnement actif"
      : "Statut du compte";

  // Titre principal de la card — adapté à l'état réel.
  const planLabel = (() => {
    if (loading) return "…";
    if (!billing) return "Aucun abonnement";
    if (accountState === "subscribed" || accountState === "past-due") {
      const p = pricingPlans.find((pl) => pl.id === billing.plan);
      return p ? p.name : billing.plan;
    }
    if (accountState === "pay-as-you-go") return "Sans abonnement";
    if (accountState === "trial") return "Essai gratuit";
    return "Compte inactif";
  })();

  // Status badge — surcharge "Résiliation en cours" si cancelAt programmé.
  const statusTone: "lime" | "amber" | "red" | "soft" = (() => {
    if (!billing) return "soft";
    if (cancelling) return "amber";
    if (billing.status === "active" || billing.status === "trialing")
      return "lime";
    if (billing.status === "past_due") return "amber";
    if (billing.status === "canceled") return "red";
    return "soft";
  })();
  const statusLabel = (() => {
    if (!billing) return "—";
    if (cancelling) return "Résiliation en cours";
    if (accountState === "pay-as-you-go") return "Crédits ponctuels";
    switch (billing.status) {
      case "active":
        return "Actif";
      case "trialing":
        return "Essai";
      case "past_due":
        return "Paiement en échec";
      case "canceled":
        return "Résilié";
      case "none":
        return accountState === "trial" ? "Essai" : "Non souscrit";
    }
  })();

  // Ligne compteur : pour un abonné, "N / quota utilisés ce mois". Pour un
  // user en essai gratuit, "X jours restants". Pour un user pay-as-you-go,
  // l'origine des crédits.
  const usageLine = (() => {
    if (loading) return "Chargement…";
    if (!billing) return "—";
    if (cancelling && billing.cancelAt) {
      return `Votre abonnement prendra fin le ${formatShortDate(billing.cancelAt.toDate())}. Vous gardez vos crédits jusque là.`;
    }
    if (accountState === "pay-as-you-go") {
      // L'user a payé un Pack ou des topups. Pas d'abonnement actif, mais
      // pas non plus en essai. Copy honnête sur ce qu'il a payé.
      return "Vous payez à la fiche, sans engagement.";
    }
    if (accountState === "trial") {
      const days = freeCreditDaysLeft ?? 0;
      const free = creditsSummary.free;
      return `${free} fiche offerte — ${days} jour${days > 1 ? "s" : ""} restant${days > 1 ? "s" : ""}.`;
    }
    if (accountState === "inactive") {
      return "Souscrivez un plan ou achetez un pack pour générer des fiches.";
    }
    // accountState === "subscribed" ou "past-due"
    const plan = pricingPlans.find((pl) => pl.id === billing.plan);
    if (!plan) return `${billing.creditsRemaining} crédits disponibles.`;

    // Edge case : après un downgrade-keep, creditsRemaining peut dépasser le
    // quota (l'user a payé une période complète à un quota plus haut, on lui
    // laisse jusqu'au prochain renouvellement). Affichage explicite pour ne
    // pas mentir avec un "0/5" qui occulterait 95 crédits.
    if (billing.creditsRemaining > plan.ficheQuota) {
      return `${billing.creditsRemaining} fiches restantes (quota ${plan.ficheQuota}/mois reset au prochain renouvellement)`;
    }
    const used = plan.ficheQuota - billing.creditsRemaining;
    return `${Math.max(0, used)} / ${plan.ficheQuota} fiches utilisées ce mois-ci.`;
  })();

  /** Données de renouvellement structurées — rendu en metadata strip
   *  (icônes inline) ou en pill ambre quand cancelling. Refactor depuis la
   *  string monolithique précédente pour permettre l'affichage par chunks. */
  const renewalInfo = (() => {
    if (
      accountState !== "subscribed" ||
      !billing ||
      billing.plan === "free" ||
      !billing.billingCycle ||
      !billing.currentPeriodEnd
    ) {
      return null;
    }
    const plan = pricingPlans.find((pl) => pl.id === billing.plan);
    if (!plan) return null;

    const monthly = priceFor(plan, billing.billingCycle);
    // Le prix Stripe annuel = engagement × 12 ; on affiche le montant
    // réellement prélevé à chaque cycle pour lever toute ambiguïté.
    const cycleAmount =
      billing.billingCycle === "engagement" ? monthly * 12 : monthly;
    const cycleSuffix = billing.billingCycle === "engagement" ? "an" : "mois";
    const nextDate = formatShortDate(billing.currentPeriodEnd.toDate());

    return {
      cancelling,
      nextDate,
      amount: formatEur(cycleAmount),
      cycleSuffix,
    };
  })();

  // CTA principal — adapté à l'état du compte. La résiliation en cours
  // prend toujours le pas (CTA dark "Reprendre"), sinon plan-change pour
  // les abonnés, "Voir les plans" pour les autres.
  const primaryCta = (() => {
    if (cancelling) {
      return (
        <Button
          variant="dark"
          size="sm"
          onClick={handleReactivate}
          disabled={reactivateBusy}
          iconRight={<Ic.Arrow size={13} />}
        >
          {reactivateBusy ? "Réactivation…" : "Reprendre l'abonnement"}
        </Button>
      );
    }
    return (
      <Button
        variant="soft"
        size="sm"
        onClick={onSeePricing}
        iconRight={<Ic.Arrow size={13} />}
      >
        {accountState === "subscribed" ? "Changer de plan" : "Voir les plans"}
      </Button>
    );
  })();

  // Liste filtrée des batches add-on (free + topup, pas le quota plan) —
  // sortie ici pour éviter de re-filter dans le JSX et pour exposer le count
  // au label de l'accordéon.
  const addOnBatches = creditsSummary.allBatches.filter((b) => b.type !== "plan");

  return (
    <div className="space-y-5">
      {/* Bloc principal : Plan (2/3) + Total disponible (1/3) côte à côte
          sur lg+. Sur mobile, le Total se replie au-dessus du Plan via
          l'ordre du DOM. Le grid n'active grid-cols qu'en présence d'un
          totalAvailable > 0 — sinon la Plan card occupe seule toute la
          largeur sans col vide à droite. */}
      <div
        className={`grid gap-5 ${
          totalAvailable > 0 ? "lg:grid-cols-3" : ""
        }`}
      >
        <Card className={`p-6 ${totalAvailable > 0 ? "lg:col-span-2" : ""}`}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0 flex-1">
              <SectionLabel>{sectionLabel}</SectionLabel>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <div className="text-[20px] font-semibold tracking-tight">
                  {planLabel}
                </div>
                <Pill tone={statusTone} icon={<Ic.Bolt size={12} />}>
                  {statusLabel}
                </Pill>
              </div>
              <p className="mt-1 text-[12.5px] text-ink-600">{usageLine}</p>

              {/* Renewal info — pill ambre si résiliation programmée,
                  metadata strip à icônes sinon. Promu visuellement vs.
                  l'ancienne fine-print line en gris. */}
              {renewalInfo && renewalInfo.cancelling && (
                <div className="mt-3">
                  <Pill tone="amber" icon={<Ic.Clock size={11} />}>
                    Fin de l'abonnement le {renewalInfo.nextDate}
                  </Pill>
                </div>
              )}
              {renewalInfo && !renewalInfo.cancelling && (
                <div className="mt-3 flex items-center gap-x-4 gap-y-1.5 flex-wrap text-[12.5px]">
                  <span className="inline-flex items-center gap-1.5 text-ink-700">
                    <Ic.Clock size={12} className="text-ink-400" />
                    Renouvellement le {renewalInfo.nextDate}
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-ink-700">
                    <Ic.Wallet size={12} className="text-ink-400" />
                    {renewalInfo.amount} € HT / {renewalInfo.cycleSuffix}
                  </span>
                </div>
              )}

              {reactivateError && (
                <div className="mt-2 text-[12.5px] text-rose-700 font-medium">
                  {reactivateError}
                </div>
              )}
            </div>
            {primaryCta}
          </div>
        </Card>

        {/* Total disponible — version compacte adaptée à la colonne 1/3.
            Plus petit que l'ancienne hero card (36px vs 42px), mais conserve
            le contraste dark+lime qui ancre visuellement la métrique. */}
        {totalAvailable > 0 && (
          <Card className="p-5 sm:p-6 !bg-ink-900 !shadow-none text-white">
            <div className="text-[10.5px] uppercase tracking-[0.14em] font-mono text-lime-300">
              Total disponible
            </div>
            <div className="mt-2 text-[36px] leading-none font-semibold tabular tracking-tight">
              {totalAvailable}
              <span className="text-[13px] font-normal text-ink-300 ml-1.5">
                fiche{totalAvailable > 1 ? "s" : ""}
              </span>
            </div>
            {creditsSummary.plan > 0 &&
              creditsSummary.free + creditsSummary.topup > 0 && (
                <div className="mt-3 pt-3 border-t border-ink-700 text-[11.5px] text-ink-200 leading-relaxed">
                  {creditsSummary.plan} du plan ·{" "}
                  {creditsSummary.free + creditsSummary.topup} add-on
                  <div className="text-[10.5px] text-ink-400 mt-0.5">
                    Add-on consommés en priorité
                  </div>
                </div>
              )}
          </Card>
        )}
      </div>

      {/* PlanSwitcher : visible uniquement quand l'user est abonné (pas en
          essai gratuit, pas en pay-as-you-go), pour qu'il puisse changer de
          plan en restant dans Arpentia. Click → portail Stripe pré-rempli
          sur la confirmation du switch. PlanSwitcher gère lui-même le cas
          `cancelling` (masqué quand résiliation programmée — l'user doit
          d'abord cliquer "Reprendre"). */}
      {accountState === "subscribed" &&
        billing &&
        billing.plan !== "free" &&
        billing.billingCycle && (
          <PlanSwitcher
            currentPlanId={billing.plan as PlanId}
            currentCycle={billing.billingCycle}
            cancelling={cancelling}
          />
        )}

      {/* Crédits add-on : liste les batches achetés (topup et packs sont
          stockés indifféremment dans `topUps[]`). On ne distingue pas
          finement le type : c'est l'expiration qui parle pour l'user.
          Note : le count droit "X fiches" est volontairement libellé
          "add-on" pour ne pas être confondu avec le quota du plan (cf.
          mini-summary en tête). */}
      {billing && (creditsSummary.topup > 0 || creditsSummary.free > 0) && (
        <Card className="p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <SectionLabel>Crédits add-on</SectionLabel>
              <p className="mt-2 text-[13px] text-ink-600 leading-relaxed max-w-[520px]">
                {creditsSummary.plan > 0
                  ? "Achetés à la volée, en pré-pack ou pack ponctuel — consommés en priorité avant le quota de votre plan."
                  : "Achetés à la volée, en pré-pack ou pack ponctuel. Consommés par ordre d'expiration la plus proche."}
              </p>
            </div>
            <div className="text-right">
              <div className="text-[28px] font-semibold tabular tracking-tight">
                {creditsSummary.free + creditsSummary.topup}
              </div>
              <div className="text-[11px] text-ink-500">
                {creditsSummary.plan > 0
                  ? `add-on (en plus du plan)`
                  : `fiche${creditsSummary.free + creditsSummary.topup > 1 ? "s" : ""} disponible${creditsSummary.free + creditsSummary.topup > 1 ? "s" : ""}`}
              </div>
            </div>
          </div>
          {/* Détail des batches replié par défaut — la majorité des users
              veut juste le total. Native <details>/<summary> pour rester
              accessible (clavier / screenreader) sans state local. */}
          {addOnBatches.length > 0 && (
            <details className="mt-4 group">
              <summary className="cursor-pointer list-none flex items-center justify-between gap-3 py-2 text-[12.5px] text-ink-600 hover:text-ink-900 select-none transition">
                <span>
                  Voir le détail des batches ({addOnBatches.length})
                </span>
                <Ic.ChevronDown
                  size={12}
                  className="transition group-open:rotate-180 text-ink-400"
                />
              </summary>
              <div className="mt-2 divide-y divide-ink-200/70 border-t border-ink-200/70">
                {addOnBatches.map((batch, i) => (
                  <div
                    key={i}
                    className="py-3 flex items-baseline justify-between gap-3 text-[13px]"
                  >
                    <div className="min-w-0">
                      <div className="font-medium text-ink-900">
                        {batch.type === "free"
                          ? "Crédit offert (inscription)"
                          : `Crédits add-on${batch.purchasedAtMs ? ` · achetés le ${formatShortDate(new Date(batch.purchasedAtMs))}` : ""}`}
                      </div>
                      <div className="text-[11.5px] text-ink-500 mt-0.5">
                        {batch.expiresAtMs
                          ? `Expire le ${formatShortDate(new Date(batch.expiresAtMs))}`
                          : ""}
                        {batch.stripeChargeId
                          ? ` · ${batch.stripeChargeId.slice(0, 14)}…`
                          : ""}
                      </div>
                    </div>
                    <div className="font-semibold tabular shrink-0">
                      {batch.remaining}
                      <span className="text-ink-500 font-normal text-[11px]">
                        {" "}/ {batch.amount}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </details>
          )}
        </Card>
      )}

      {/* Customer Portal Stripe — moyens de paiement, factures, résiliation.
          Le portail délègue à Stripe la self-service complète ; nos webhooks
          resynchronisent /users/{uid}/billing/current au retour. */}
      {hasStripeCustomer ? (
        <Card className="p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <SectionLabel>Moyens de paiement & factures</SectionLabel>
              <h3 className="mt-2 text-[17px] font-semibold tracking-tight">
                Gérer mon abonnement
              </h3>
              <p className="mt-2 text-[13.5px] text-ink-600 leading-relaxed max-w-[520px]">
                Ouvre le portail Stripe sécurisé pour mettre à jour votre carte
                bancaire, télécharger vos factures, changer de plan ou résilier
                votre abonnement.
              </p>
              {portalError && (
                <div className="mt-3 text-[12.5px] text-rose-700 font-medium">
                  {portalError}
                </div>
              )}
            </div>
            <Button
              variant="soft"
              size="md"
              onClick={handleOpenPortal}
              disabled={portalBusy}
              iconRight={<Ic.Arrow size={13} />}
            >
              {portalBusy ? "Ouverture…" : "Ouvrir le portail"}
            </Button>
          </div>
        </Card>
      ) : (
        <EmptyTab
          icon={<Ic.Receipt size={18} />}
          title="Historique et moyens de paiement"
          description="Aucun historique pour l'instant — souscrivez un plan ou achetez un pack pour accéder à vos factures Stripe et à la gestion de votre moyen de paiement."
        />
      )}
    </div>
  );
}

/** Format court FR ("12 mai 2026") pour les dates de billing — assez compact
 *  pour rentrer dans une row de liste mais sans abréviation ambiguë. */
function formatShortDate(d: Date): string {
  return d.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/* ---------- Données personnelles (RGPD Art. 15/17/20) ---------- */

function DonneesTab({
  onShowPolicy,
  onSignedOut,
}: {
  onShowPolicy: () => void;
  onSignedOut: () => void;
}) {
  const { user } = useAuth();
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportSuccess, setExportSuccess] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  async function handleExport() {
    setExporting(true);
    setExportError(null);
    setExportSuccess(null);
    try {
      await exportAndDownloadUserData();
      setExportSuccess("Export téléchargé avec succès.");
    } catch (e) {
      if (e instanceof RateLimitError) {
        const hours = Math.ceil(e.retryAfterSec / 3600);
        setExportError(
          `Un export a déjà été généré récemment. Réessayez dans ${hours} h.`,
        );
      } else {
        const err = e as { code?: string; message?: string };
        if (err.code === "functions/not-found") {
          setExportError(
            "La fonction d'export n'est pas encore déployée en production.",
          );
        } else {
          setExportError(err.message ?? "Erreur inconnue.");
        }
      }
    } finally {
      setExporting(false);
    }
  }

  if (!user) {
    return (
      <Card className="p-6 sm:p-10 text-center">
        <div className="w-14 h-14 rounded-full bg-ink-50 hairline grid place-items-center mx-auto text-ink-500">
          <Ic.Shield size={18} />
        </div>
        <h2 className="mt-4 text-[18px] font-semibold tracking-tight">
          Connexion requise
        </h2>
        <p className="mt-2 text-[13px] text-ink-600 leading-relaxed max-w-[480px] mx-auto">
          Connectez-vous pour gérer vos données personnelles.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      <Card className="p-6">
        <SectionLabel>Vos droits</SectionLabel>
        <div className="mt-2 text-[14px] text-ink-700 leading-relaxed">
          Vous disposez des droits d'accès, de rectification, d'effacement, de
          portabilité, d'opposition et de limitation sur vos données
          personnelles (RGPD art. 15 à 22).{" "}
          <button
            onClick={onShowPolicy}
            className="underline decoration-ink-300 underline-offset-2 hover:text-ink-900"
          >
            Voir la politique de confidentialité complète
          </button>
          .
        </div>
      </Card>

      {/* Export */}
      <Card className="p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <SectionLabel>Exporter mes données</SectionLabel>
            <h3 className="mt-2 text-[17px] font-semibold tracking-tight">
              Obtenir une copie de vos données
            </h3>
            <p className="mt-2 text-[13.5px] text-ink-600 leading-relaxed max-w-[520px]">
              Télécharge un fichier JSON contenant votre profil, vos fiches,
              vos dossiers, votre abonnement et l'historique d'usage — format
              structuré et portable (RGPD art. 15 + 20). Limite&nbsp;: 1 export
              toutes les 24 heures.
            </p>
            {exportSuccess && (
              <div className="mt-3 text-[12.5px] text-forest font-medium">
                ✓ {exportSuccess}
              </div>
            )}
            {exportError && (
              <div className="mt-3 text-[12.5px] text-rose-700 font-medium">
                {exportError}
              </div>
            )}
          </div>
          <Button
            variant="soft"
            size="md"
            onClick={handleExport}
            disabled={exporting}
            icon={<Ic.Download size={13} />}
          >
            {exporting ? "Génération…" : "Télécharger mes données"}
          </Button>
        </div>
      </Card>

      {/* Suppression */}
      <Card className="p-6 border-rose-200 bg-rose-50/30">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <SectionLabel className="!text-rose-700">
              Zone sensible
            </SectionLabel>
            <h3 className="mt-2 text-[17px] font-semibold tracking-tight text-ink-900">
              Supprimer mon compte
            </h3>
            <p className="mt-2 text-[13.5px] text-ink-700 leading-relaxed max-w-[520px]">
              Supprime définitivement votre compte, vos fiches, vos dossiers et
              votre abonnement. Cette action est{" "}
              <strong>irréversible</strong>. Les factures déjà émises sont
              conservées 10 ans au titre de l'obligation comptable.
            </p>
          </div>
          <Button
            variant="soft"
            size="md"
            onClick={() => setDeleteOpen(true)}
            icon={<Ic.Trash size={13} />}
            className="!bg-rose-100 hover:!bg-rose-200 !text-rose-900"
          >
            Supprimer mon compte
          </Button>
        </div>
      </Card>

      {deleteOpen && (
        <DeleteAccountDialog
          email={user.email ?? ""}
          provider={user.providerData[0]?.providerId ?? "password"}
          onClose={() => setDeleteOpen(false)}
          onDeleted={onSignedOut}
        />
      )}
    </div>
  );
}

function DeleteAccountDialog({
  email,
  provider,
  onClose,
  onDeleted,
}: {
  email: string;
  provider: string;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [confirmEmail, setConfirmEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const needsPassword = provider === "password";
  const emailMatches =
    confirmEmail.trim().toLowerCase() === email.trim().toLowerCase();
  const canSubmit = emailMatches && (!needsPassword || password.length > 0);

  async function handleConfirm() {
    setBusy(true);
    setError(null);
    try {
      // Ré-auth systématique juste avant — garantit le auth_time < 5 min
      // exigé par la callable côté serveur.
      await reauthenticateCurrentUser(needsPassword ? password : undefined);
      await deleteAccountDefinitively(email);
      onDeleted();
    } catch (e) {
      const err = e as { code?: string; message?: string };
      if (err.code === "auth/wrong-password" || err.code === "auth/invalid-credential") {
        setError("Mot de passe incorrect.");
      } else if (err.code === "auth/popup-closed-by-user") {
        setError("Ré-authentification annulée.");
      } else if (err.code === "requires-recent-login") {
        setError(
          "Votre session est trop ancienne. Relancez l'opération après reconnexion.",
        );
      } else if (err.code === "functions/not-found") {
        setError(
          "La fonction de suppression n'est pas encore déployée en production.",
        );
      } else {
        setError(err.message ?? "Erreur lors de la suppression.");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[200] bg-ink-900/40 backdrop-blur-sm grid place-items-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <Card className="w-full max-w-[520px] p-6 sm:p-7">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-rose-100 grid place-items-center text-rose-700">
            <Ic.Trash size={15} />
          </div>
          <h2 className="text-[18px] font-semibold tracking-tight">
            Supprimer définitivement ?
          </h2>
        </div>
        <p className="mt-3 text-[13.5px] text-ink-700 leading-relaxed">
          Votre compte, vos fiches, vos dossiers et votre abonnement seront{" "}
          <strong>effacés de façon irréversible</strong>. Les logs d'usage sont
          anonymisés. Les factures déjà émises sont conservées 10 ans pour
          respecter l'obligation comptable.
        </p>

        <label className="mt-5 block">
          <div className="text-[12.5px] font-medium text-ink-700">
            Tapez votre email pour confirmer
          </div>
          <div className="text-[11.5px] text-ink-500 font-mono mt-0.5">
            {email}
          </div>
          <input
            value={confirmEmail}
            onChange={(e) => setConfirmEmail(e.target.value)}
            placeholder="votre@email.fr"
            autoFocus
            className="mt-2 w-full h-10 px-3 rounded-lg bg-paper hairline text-[13.5px] outline-none focus:ring-2 focus:ring-rose-200"
          />
        </label>

        {needsPassword && (
          <label className="mt-4 block">
            <div className="text-[12.5px] font-medium text-ink-700">
              Mot de passe actuel (ré-authentification)
            </div>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="mt-2 w-full h-10 px-3 rounded-lg bg-paper hairline text-[13.5px] outline-none focus:ring-2 focus:ring-rose-200"
            />
          </label>
        )}

        {!needsPassword && (
          <div className="mt-4 text-[12px] text-ink-500 leading-relaxed">
            Une fenêtre Google s'ouvrira pour ré-authentifier votre session
            avant la suppression.
          </div>
        )}

        {error && (
          <div className="mt-4 text-[12.5px] text-rose-700 font-medium">
            {error}
          </div>
        )}

        <div className="mt-5 flex items-center justify-end gap-2">
          <Button
            variant="soft"
            size="sm"
            onClick={onClose}
            disabled={busy}
          >
            Annuler
          </Button>
          <Button
            variant="soft"
            size="sm"
            onClick={handleConfirm}
            disabled={!canSubmit || busy}
            className="!bg-rose-600 hover:!bg-rose-700 !text-white disabled:!bg-rose-300"
          >
            {busy ? "Suppression…" : "Supprimer définitivement"}
          </Button>
        </div>
      </Card>
    </div>
  );
}

/* ---------- Préférences ---------- */

function PreferencesTab() {
  return (
    <EmptyTab
      icon={<Ic.Settings size={18} />}
      title="Préférences d'affichage"
      description="Bientôt : notifications email, langue, unités par défaut (m² / ha), format d'export préféré."
    />
  );
}

/* ---------- shared ---------- */

function EmptyTab({
  icon,
  title,
  description,
}: {
  icon: ReactNode;
  title: string;
  description: string;
}) {
  return (
    <Card className="p-6 sm:p-10 text-center">
      <div className="w-14 h-14 rounded-full bg-ink-50 hairline grid place-items-center mx-auto text-ink-500">
        {icon}
      </div>
      <h2 className="mt-4 text-[18px] font-semibold tracking-tight">{title}</h2>
      <p className="mt-2 text-[13px] text-ink-600 leading-relaxed max-w-[480px] mx-auto">
        {description}
      </p>
    </Card>
  );
}

const Row = ({
  label,
  value,
}: {
  label: string;
  value: ReactNode;
}) => (
  <div className="flex items-start gap-4">
    <dt className="w-[90px] sm:w-[140px] shrink-0 text-ink-500">{label}</dt>
    <dd className="flex-1 min-w-0 text-ink-900 break-words">{value}</dd>
  </div>
);

function formatDate(d: Date | null): string {
  if (!d) return "—";
  return d.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
